import dayjs from 'dayjs';

import type { Appointments, Locations } from '@gql/base-types';

/**
 * Pure state model behind MobileServiceAppointmentDetails' inline card
 * editing (#2013): the draft snapshot, per-card dirty checks, and the
 * one-mutation changes payload. Kept free of React/MUI so the mapping —
 * especially the datetime-local ⇄ ISO conversion the dispatch SMS depends
 * on — stays unit-testable.
 */

/** Formats a phone number progressively as the user types (AddAppointmentDialog's mask). */
export const formatPhoneProgressive = (value: string): string => {
  const raw = value.replace(/\D/g, '');
  const digits = raw.length > 10 && raw.startsWith('1') ? raw.slice(1) : raw;
  const len = digits.length;
  if (len === 0) return '';
  if (len <= 3) return `(${digits}`;
  if (len <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

/**
 * "Today, 1:30 PM · Thu, Apr 16, 2026" (secondary anchors the relative day)
 * or just "Thu, Apr 16, 2026, 1:30 PM" — no secondary when the primary
 * already names the date.
 */
export const formatWhen = (time?: string | null) => {
  if (!time) return { primary: 'No time set', secondary: null as string | null };
  const t = dayjs(time);
  const relativeDay = t.isSame(dayjs(), 'day') ? 'Today' : t.isSame(dayjs().add(1, 'day'), 'day') ? 'Tomorrow' : null;
  if (relativeDay) {
    return { primary: `${relativeDay}, ${t.format('h:mm A')}`, secondary: t.format('ddd, MMM D, YYYY') };
  }
  return { primary: t.format('ddd, MMM D, YYYY, h:mm A'), secondary: null as string | null };
};

export type CardId = 'consumer' | 'location' | 'vehicle' | 'service';
export const CARD_IDS: CardId[] = ['consumer', 'location', 'vehicle', 'service'];

export type Draft = {
  consumer: { name: string; phone: string };
  location: Locations | null;
  vehicle: { year: string; make: string; model: string; vin: string; ro: string };
  service: { time: string; notes: string; minutes: string };
};

export const buildDraft = (a: Appointments): Draft => ({
  consumer: {
    name: a.consumer_name || '',
    phone: formatPhoneProgressive(a.consumer_phone || ''),
  },
  location: (a.pickup as unknown as Locations) || null,
  vehicle: {
    year: a.vehicle_year != null ? String(a.vehicle_year) : '',
    make: a.vehicle_make || '',
    model: a.vehicle_model || '',
    vin: a.vehicle_vin || '',
    ro: (a as { ro_number?: string | null }).ro_number || '',
  },
  service: {
    // datetime-local format, matching AddAppointmentDialog's field.
    time: a.appointment_time ? dayjs(a.appointment_time).format('YYYY-MM-DDTHH:mm') : '',
    notes: a.driver_notes || '',
    minutes: String(
      Number((a.config as { estimated_service_minutes?: unknown })?.estimated_service_minutes) || 30
    ),
  },
});

export const cardEquals = (id: CardId, a: Draft, b: Draft): boolean => {
  if (id === 'location') return (a.location?.id ?? null) === (b.location?.id ?? null);
  return JSON.stringify(a[id]) === JSON.stringify(b[id]);
};

/**
 * The columns one save writes for the given dirty cards — everything except
 * the updatedat/updatedby audit pair the component stamps at send time.
 */
export const buildCardChanges = (
  toSave: CardId[],
  draft: Draft,
  appointment: Appointments
): Record<string, unknown> => {
  const changes: Record<string, unknown> = {};
  if (toSave.includes('consumer')) {
    changes.consumer_name = draft.consumer.name.trim() || null;
    changes.consumer_phone = draft.consumer.phone.replace(/\D/g, '') || null;
  }
  if (toSave.includes('location')) {
    changes.pickup_location_id = draft.location!.id;
  }
  if (toSave.includes('vehicle')) {
    const year = parseInt(draft.vehicle.year, 10);
    changes.vehicle_year = Number.isFinite(year) ? year : null;
    changes.vehicle_make = draft.vehicle.make.trim() || null;
    changes.vehicle_model = draft.vehicle.model.trim() || null;
    changes.vehicle_vin = draft.vehicle.vin.trim() || null;
    changes.ro_number = draft.vehicle.ro.trim() || null;
  }
  if (toSave.includes('service')) {
    // datetime-local gives an offset-less string; dayjs applies local-time
    // semantics consistently (same reasoning as AddAppointmentDialog).
    changes.appointment_time = draft.service.time ? dayjs(draft.service.time).toISOString() : null;
    changes.driver_notes = draft.service.notes.trim() || null;
    // Merge so sibling config keys written by other flows survive the edit.
    changes.config = {
      ...((appointment.config as Record<string, unknown>) ?? {}),
      estimated_service_minutes: parseInt(draft.service.minutes, 10) || 30,
    };
  }
  return changes;
};
